/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../api/api';
import { getTokenData } from 'src/utils/api';
import { LOADING_STATUSES } from '../../../constants/statuses';
import { isResponseSuccess, formatFilters } from '../../../utils/api';
import { INITIAL_FILTERS_STATE, sortFieldMap} from '../constants/promoCodes';

export const loadPromoCodes = createAsyncThunk(
  'promoCodes/loadPromoCodes',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const { filters, page, per } = getState().promoCodesReducer;
      const formattedFilters = formatFilters(filters);
      const response = await API(getTokenData()).PROMO_CODES.load({
        ...formattedFilters,
        page: page + 1,
        per,
      });
      if (isResponseSuccess(response.status)) {
        return {
          data: response.data,
          totalItems: response.total,
        };
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const loadPromoCodesById = createAsyncThunk(
  'promoCodes/loadPromoCodesById',
  async ({
    id,
  }, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).PROMO_CODES.loadById(id);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const createPromoCodes = createAsyncThunk(
  'promoCodes/createPromoCodes',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).PROMO_CODES.create({
        ...data,
        discount: {
          value: Number(data?.discount?.value || 0),
          kind: Number(data?.discount?.kind || 0),
          quantity: Number(data?.discount?.quantity || 0),
        },
      });
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const updatePromoCodes = createAsyncThunk(
  'promoCodes/updatePromoCodes',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).PROMO_CODES.update({
        ...data,
        discount: {
          value: Number(data?.discount?.value || 0),
          kind: Number(data?.discount?.kind || 0),
          quantity: Number(data?.discount?.quantity || 0),
        },
      });
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const deletePromoCodes = createAsyncThunk(
  'promoCodes/deletePromoCodes',
  async (id, {
    getState,
    dispatch,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).PROMO_CODES.delete(id);
      if (isResponseSuccess(response.status)) {
        dispatch(loadPromoCodes());
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

const initialState = {
  entity: [],
  page: 0,
  per: 100,
  totalItems: 0,
  status: LOADING_STATUSES.IDLE,
  error: null,
  selected: {},
  selectedStatus: LOADING_STATUSES.IDLE,
  filters: INITIAL_FILTERS_STATE,
};

const promoCodesSlice = createSlice({
  name: 'promoCodes',
  initialState,
  reducers: {
    setSelectedPromoCode: (state, action) => {
      state.selected = action.payload;
    },
    setPageData: (state, action) => {
      state.page = action.payload.page;
      state.per = action.payload.per;
    },
    resetPromoCodes: () => initialState,
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadPromoCodes.pending, (state) => {
        state.status = LOADING_STATUSES.LOADING;
      })
      .addCase(loadPromoCodes.fulfilled, (state, action) => {
        state.entity = action.payload?.data || [];
        state.totalItems = action.payload?.totalItems || 0;
        state.status = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadPromoCodes.rejected, (state) => {
        state.status = LOADING_STATUSES.FAILED;
      })
      .addCase(loadPromoCodesById.pending, (state) => {
        state.selectedStatus = LOADING_STATUSES.LOADING;
      })
      .addCase(loadPromoCodesById.fulfilled, (state, action) => {
        state.selected = action.payload || {};
        state.selectedStatus = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadPromoCodesById.rejected, (state) => {
        state.selectedStatus = LOADING_STATUSES.FAILED;
      });
  },
});

export const {
  setPageData,
  resetPromoCodes,
  setSelectedPromoCode,
  setFilters,
} = promoCodesSlice.actions;
export default promoCodesSlice.reducer;

export const promoCodesChangePageAction = createAsyncThunk(
  'promoCodes/promoCodesChangePageAction',
  async ({
    page = 0,
    per = 0,
  }, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(setPageData({
        page,
        per,
      }));
      await dispatch(loadPromoCodes());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const promoCodesFilterApply = createAsyncThunk(
  'promoCodes/filterApply',
  async (newFilters, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      const mappedSortBy = sortFieldMap[newFilters.sort_by] || newFilters.sort_by;
      await dispatch(setFilters({
        ...newFilters,
        sort_by: mappedSortBy,
      }));
      await dispatch(loadPromoCodes());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);
